import { useEffect, useMemo, useState } from 'react';
import type { ContractCompanyRow, ContractFieldSource, ContractPreviewParagraph, ContractSourceOption, ContractTemplateVersionView } from '@kassa/shared';
import { api, ApiError } from '../api';
import { contractVersionFileUrl } from '../api/contracts';
import { normalizeFieldName, type PreviewMode } from '../contracts';
import { formatDate } from '../format';
import { PageHeader } from '../components/ListPage';
import { ContractPreview } from './ContractPreview';

// Ekran 21 «Müqavilə şablonu» (KT-82, docs/dizayn/Sablon.dc.html): #sahələr#, mənbə, baxış, «Mətni sahə et», versiyalar.
// Hər yadda saxlama yeni versiyadır; köhnə versiya ilə bağlanmış müqavilələr dəyişmir.

const errText = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : 'Xəta baş verdi');
const today = () => new Date().toISOString().slice(0, 10);

type Draft = Record<string, { source: ContractFieldSource; sourceKey: string | null }>;

export function ContractTemplate({ versionId, onBack }: { versionId: string; onBack: () => void }) {
  const [v, setV] = useState<ContractTemplateVersionView | null>(null);
  const [sources, setSources] = useState<ContractSourceOption[]>([]);
  const [draft, setDraft] = useState<Draft>({});
  const [mode, setMode] = useState<PreviewMode>('template');
  const [companies, setCompanies] = useState<ContractCompanyRow[]>([]);
  const [buyer, setBuyer] = useState('');
  const [filled, setFilled] = useState<{ preview: ContractPreviewParagraph[]; missing: string[] } | null>(null);
  const [conv, setConv] = useState(false);
  const [selText, setSelText] = useState('');
  const [newTag, setNewTag] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<number | null>(null);

  const apply = (view: ContractTemplateVersionView) => {
    setV(view);
    setDraft(Object.fromEntries(view.fields.map((f) => [f.tag, { source: f.source, sourceKey: f.sourceKey }])));
    setFilled(null);
  };

  useEffect(() => {
    api
      .contractVersion(versionId)
      .then(apply)
      .catch((e: unknown) => setError(errText(e)));
    api.contractSources().then(setSources).catch(() => undefined);
    api
      .contractCompanies('')
      .then((c) => {
        setCompanies(c);
        if (c[0]) setBuyer(c[0].id);
      })
      .catch(() => undefined);
  }, [versionId]);

  const dirty = useMemo(() => !!v && v.fields.some((f) => draft[f.tag] && (draft[f.tag].source !== f.source || draft[f.tag].sourceKey !== f.sourceKey)), [v, draft]);

  // Doldurulmuş baxış: seçilmiş alıcı ilə (yadda saxlanmış versiyanın mənbələri)
  useEffect(() => {
    if (mode !== 'filled' || !v || !buyer) return;
    api
      .contractPreview({ templateVersionId: v.id, counterpartyId: buyer, legalEntityId: null, number: '', signedOn: today(), validUntil: null, paymentTerm: null, manualValues: {} })
      .then((p) => {
        setFilled({ preview: p.preview, missing: p.missing });
        setError(null);
      })
      .catch((e: unknown) => setError(errText(e)));
  }, [mode, v, buyer]);

  const run = async (f: () => Promise<ContractTemplateVersionView>, okVersion = true) => {
    setBusy(true);
    setError(null);
    try {
      const view = await f();
      apply(view);
      if (okVersion) setSaved(view.versionNo);
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const save = () =>
    v &&
    run(() =>
      api.contractVersionFields(v.id, {
        fields: v.fields.map((f) => ({ tag: f.tag, source: draft[f.tag].source, sourceKey: draft[f.tag].source === 'manual' ? null : draft[f.tag].sourceKey })),
        note: null,
      }),
    );

  const makeField = () => {
    const tag = normalizeFieldName(newTag);
    if (!v) return;
    if (!selText.trim()) return setError('Baxışda sahəyə çeviriləcək mətni seçin');
    if (!tag) return setError('Sahə adı yalnız hərf, rəqəm və _ ola bilər (boşluqsuz)');
    void run(() => api.contractMakeField(v.id, selText, tag)).then(() => {
      setSelText('');
      setNewTag('');
    });
  };

  const upload = (file: File | undefined) => file && v && run(() => api.contractTemplateUpload(v.templateId, file));

  if (!v) {
    return (
      <div className="page">
        <PageHeader title="Müqavilə şablonu" />
        {error ? (
          <p className="bad" role="alert">
            {error}
          </p>
        ) : (
          <p className="muted">Yüklənir…</p>
        )}
      </div>
    );
  }

  const optionsFor = (s: ContractFieldSource) => sources.filter((o) => o.source === s);
  const latest = v.versionNo === v.latestVersionNo;
  return (
    <div className="page">
      <PageHeader
        title={v.templateName}
        sub={`${v.kind === 'main' ? 'Əsas müqavilə' : 'Əlavə'} · versiya ${v.versionNo}${latest ? '' : ` (son: ${v.latestVersionNo})`}`}
      />
      <div className="form-actions contract-toolbar">
            <button type="button" className="btn-secondary" onClick={onBack}>
              ← Müqavilələr
            </button>
            <label className="btn-secondary contract-upload">
              Yeni versiya yüklə (.docx)
              <input type="file" accept=".docx" className="sr-only" disabled={busy} onChange={(e) => void upload(e.target.files?.[0])} />
            </label>
            <a className="btn-secondary" href={contractVersionFileUrl(v.id)} download>
              Word faylı
            </a>
            <button type="button" className="btn-primary" disabled={busy || !dirty} onClick={() => void save()}>
              Yadda saxla
            </button>
      </div>
      <ol className="contract-steps">
        <li>
          <strong>Word-da yazın</strong> — dəyişən yerə #ad#
        </li>
        <li>
          <strong>Yükləyin</strong> — sistem sahələri özü tapır
        </li>
        <li>
          <strong>Mənbə seçin</strong> — kartdan və ya əl ilə
        </li>
        <li>
          <strong>Baxın, saxlayın</strong> — sonra təkrar dəyişin
        </li>
      </ol>
      {saved !== null && (
        <p className="ok" role="status">
          Versiya {saved} yadda saxlandı. Dəyişməyən mətn olduğu kimi qaldı; köhnə versiyalar tarixçədə qalır.
        </p>
      )}
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      <div className="contract-layout contract-layout-tpl">
        <section className="card" aria-label="Dəyişən sahələr">
          <h2>Dəyişən sahələr · {v.fields.length}</h2>
          {v.fields.length === 0 && <p className="muted">Faylda #sahə# yoxdur. Word-da dəyişən yerə #ad# yazıb yeni versiya yükləyin və ya «Mətni sahə et» edin.</p>}
          <div className="table-wrap">
            <table className="data-table" aria-label="Sahələr">
              <thead>
                <tr>
                  <th scope="col">Sahə</th>
                  <th scope="col">Haradan dolur</th>
                </tr>
              </thead>
              <tbody>
                {v.fields.map((f) => {
                  const d = draft[f.tag] ?? { source: f.source, sourceKey: f.sourceKey };
                  const value = d.source === 'manual' ? 'manual' : (d.sourceKey ?? '');
                  return (
                    <tr key={f.tag}>
                      <td className="mono">
                        #{f.tag}#{f.occurrences > 1 && <span className="muted small"> ×{f.occurrences}</span>}
                      </td>
                      <td>
                        <select
                          aria-label={`#${f.tag}# mənbəyi`}
                          value={value}
                          onChange={(e) => {
                            const key = e.target.value;
                            setDraft((x) => ({ ...x, [f.tag]: key === 'manual' ? { source: 'manual', sourceKey: null } : { source: key.split('.')[0] as ContractFieldSource, sourceKey: key } }));
                          }}
                        >
                          <option value="manual">Əl ilə (hər müqavilədə yazılır)</option>
                          {(['buyer', 'own', 'auto'] as const).map((s) => (
                            <optgroup key={s} label={s === 'buyer' ? 'Alıcı kartı' : s === 'own' ? 'Bizim rekvizitlər' : 'Müqavilə'}>
                              {optionsFor(s).map((o) => (
                                <option key={o.key} value={o.key}>
                                  {o.label}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="muted small">Yeni sahə: Word-da yerinə #ad# yazıb yeni versiya yükləyin və ya baxışda «Mətni sahə et» rejimini açıb sözü seçin.</p>
          <h3>Faylın yoxlanışı</h3>
          {v.warnings.length === 0 ? (
            <p className="ok">Sahə adları qaydasındadır.</p>
          ) : (
            <ul>
              {v.warnings.map((w) => (
                <li key={w} className="bad">
                  {w} — tək # ilə yazılıb; Word-da {w}# kimi düzəldin
                </li>
              ))}
            </ul>
          )}
          <h3>Versiyalar</h3>
          <ul className="contract-history">
            {v.history.map((h) => (
              <li key={h.id}>
                <span className="mono">v{h.versionNo}</span> · {h.note ?? 'ilk yükləmə'} <span className="muted small">{formatDate(h.createdAt)}</span>
              </li>
            ))}
          </ul>
          <p className="muted small">Köhnə versiya ilə bağlanmış müqavilələr dəyişmir — yeni versiya yalnız yeni müqavilələrə tətbiq olunur.</p>
        </section>
        <section className="card" aria-label="Baxış">
          <div className="card-head">
            <div className="tabs" role="tablist" aria-label="Baxış rejimi">
              <button type="button" role="tab" aria-selected={mode === 'template'} className={mode === 'template' ? 'tab active' : 'tab'} onClick={() => setMode('template')}>
                Şablon
              </button>
              <button type="button" role="tab" aria-selected={mode === 'filled'} className={mode === 'filled' ? 'tab active' : 'tab'} onClick={() => setMode('filled')}>
                Doldurulmuş
              </button>
            </div>
            <label className="contract-conv">
              <input type="checkbox" checked={conv} onChange={(e) => setConv(e.target.checked)} /> Mətni sahə et
            </label>
          </div>
          {mode === 'filled' && (
            <label className="field">
              <span>Baxış üçün alıcı</span>
              <select value={buyer} onChange={(e) => setBuyer(e.target.value)}>
                {companies.length === 0 && <option value="">VÖEN-li alıcı yoxdur</option>}
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {conv && (
            <div className="contract-conv-form">
              <p className="muted small">Baxışda sözü siçanla seçin, sonra sahənin adını yazın.</p>
              <label className="field">
                <span>Seçilmiş mətn</span>
                <input value={selText} onChange={(e) => setSelText(e.target.value)} maxLength={500} />
              </label>
              <label className="field">
                <span>Sahə adı</span>
                <input value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="məs. şəhər" maxLength={40} />
              </label>
              <button type="button" className="btn-primary" disabled={busy} onClick={makeField}>
                Sahə et
              </button>
            </div>
          )}
          {mode === 'filled' && filled && filled.missing.length > 0 && (
            <p className="bad small">Boş sahələr (müqavilədə yazılmalıdır): {filled.missing.map((t) => `#${t}#`).join(', ')}</p>
          )}
          <ContractPreview
            paragraphs={mode === 'filled' && filled ? filled.preview : v.preview}
            mode={mode === 'filled' && filled ? 'filled' : 'template'}
            label="Müqavilə mətni"
            onMouseUp={
              conv
                ? () => {
                    const s = window.getSelection()?.toString().trim() ?? '';
                    if (s) setSelText(s.slice(0, 500));
                  }
                : undefined
            }
          />
        </section>
      </div>
    </div>
  );
}
